import { TXN_STATUS } from "./../paymentService";
import { FrameParser, createReqFromMsg, type FrameResult } from "./utils";
import { Http_postLogReport } from "@/http/apiFunction";
import {
  EventName,
  StatusCode,
  PaymentAppId,
  TransactionStatus,
  POS_STATUS_DESC,
  RESP_CODE_DESC,
} from "./type";
import type {
  BaseRequest,
  BaseResponse,
  LinkTestRequest,
  LinkTestResponse,
  SaleRequest,
  SaleResponse,
  RefundRequest,
  RefundResponse,
  VoidRequest,
  VoidResponse,
  AbortRequest,
  AbortResponse,
  HealthCheckRequest,
  HealthCheckResponse,
  SettlementRequest,
  SettlementResponse,
  RetrievalResponse,
} from "./type";
import dayjs from "dayjs";
import { set } from "lodash";

/**
 * N5 POS 机配置选项
 */
export interface N5PosConfig {
  /** 串口配置 */
  serialConfig?: SerialOptions;
  /** 默认超时时间（毫秒） */
  defaultTimeout?: number;
  /** ACK/NAK 响应超时时间（毫秒） */
  ackTimeout?: number;
  /** 拼帧超时时间（毫秒） */
  frameTimeout?: number;
  /** 最大重试次数 */
  maxRetries?: number;
  /** 是否启用调试日志 */
  debug?: boolean;
  /** 心跳间隔时间（毫秒） */
  heartbeatInterval?: number;
}

/**
 * POS 机串口连接状态
 * 根据设计方案实现更精细的连接状态管理
 */
export enum PosConnectionStatus {
  /** 未连接 */
  DISCONNECTED = "disconnected",
  /** 连接中 */
  CONNECTING = "connecting",
  /** 已连接 */
  CONNECTED = "connected",
  /** 断开连接中 */
  DISCONNECTING = "disconnecting",
  /** 连接错误 */
  ERROR = "error",
}

/**
 * POS 机健康状态
 */
export enum PosHealthStatus {
  /** 正常 */
  SUCCESS = "00",
  /** 主机拒绝 */
  FAIL = "10",
  /** ECR繁忙 POS机在非首页情况 */
  ECR_BUSY = "97",
  /** 未知状态 */
  UNKNOWN = "99",
}

/**
 * 事务处理状态
 * 根据设计方案实现完整的事务状态机
 */
export enum TransactionState {
  /** 空闲状态 */
  IDLE = "idle",
  /** 等待 ACK/NAK 响应 */
  WAITING_FOR_ACK = "waiting_for_ack",
  /** 等待最终响应 */
  WAITING_FOR_RESPONSE = "waiting_for_response",
  /** 正在取消交易 */
  ABORTING = "aborting",
  /** 正在查找交易结果 */
  RETRIEVING = "retrieving",
}

/**
 * 交易请求状态
 */
export type TransactionRequestStatus =
  | "pending" // 等待发送
  | "sent" // 已发送
  | "ack_received" // 已收到ACK
  | "completed" // 已完成
  | "failed" // 失败
  | "aborted" // 已取消
  | "timeout"; // 超时

/**
 * 交易记录接口
 */
export interface TransactionRecord {
  /** 交易ID */
  txnId?: string;
  /** 交易类型 */
  eventName: EventName;
  /** 交易金额 */
  amount?: number;
  /** 交易时间 */
  timestamp: number;
  /** 交易状态码 (如 "00" 表示成功) */
  status?: string;
  /** 请求状态 */
  requestStatus: TransactionRequestStatus;
  /** 错误信息 */
  errorMessage?: string;
  /** 重试次数 */
  retryCount?: number;
  /** 响应数据 */
  responseData?: any;
}

/**
 * 交易记录事件类型
 */
export type TransactionRecordEventType = "added" | "updated" | "cleared";

/**
 * 交易记录事件接口
 */
export interface TransactionRecordEvent {
  /** 事件类型 */
  type: TransactionRecordEventType;
  /** 单个记录（用于 added 和 updated） */
  record?: TransactionRecord;
  /** 所有记录（用于 cleared） */
  records: TransactionRecord[];
}

/**
 * 连接状态变化事件接口
 */
export interface ConnectionStatusEvent {
  /** 事件类型 */
  type: "connectionChange";
  /** 连接状态 */
  connectionStatus: PosConnectionStatus;
  /** 健康状态 */
  healthStatus: PosHealthStatus;
  /** 状态变化时间戳 */
  timestamp: number;
  /** 错误信息（如果有） */
  error?: string;
}

/**
 * 交易结果接口
 */
export interface TransactionResult<T = BaseResponse> {
  /** 是否成功 */
  success: boolean;
  /** 响应数据 */
  data?: T;
  /** 错误信息 */
  msg?: string;
  /** 状态 */
  status:
    | "COMPLETE"
    | "TIMEOUT"
    | "ERROR"
    | "EOF"
    | "ACK"
    | "NAK"
    | "INCOMPLETE";
  /** 状态码 */
  statusCode?: StatusCode | TransactionStatus;
  /** 交易记录 */
  transactionRecord?: TransactionRecord;
}

/**
 * 请求项接口
 * 增强请求队列管理
 */
interface RequestItem<
  T extends BaseResponse | AbortResponse = BaseResponse | AbortResponse
> {
  /** 请求ID */
  id: string;
  /** 请求数据 */
  request: BaseRequest | AbortRequest;
  /** 请求时间戳 */
  timestamp: number;
  /** 超时时间 */
  timeout: number;
  /** 重试次数 */
  retryCount: number;
  /** 最大重试次数 */
  maxRetries: number;
  /** 状态 */
  status: TransactionRequestStatus;
  /** Promise resolve */
  resolve: (value: TransactionResult<T>) => void;
  /** Promise reject */
  reject: (reason: any) => void;
  /** 超时定时器 */
  timeoutTimer?: number;
  /** ACK 超时定时器 */
  ackTimeoutTimer?: number;
  /** 优先级（用于紧急请求如 ABORT） */
  priority?: "normal" | "high";
  /**
   * 轮询策略（当主请求收到 ACK 且允许重试时启用）
   * - ackReceived: 是否已收到主请求的 ACK（首次 ACK 到来即置为 true）
   * - intervalMs: 轮询间隔毫秒数（默认 5000ms）
   * - maxAttempts: 最大轮询次数（默认 12 次）
   * - attempts: 当前已尝试的轮询次数
   * - timer: 下一次轮询的定时器句柄
   * - active: 轮询是否处于激活状态（启动后为 true，停止后为 false）
   *
   * 说明：轮询基于 RETRIEVAL 请求直发设备（不走队列，超时 4s，0 重试），用于在主交易超时前主动查询最终结果；
   * 当轮询结果为忽略条件（STATUS=97 或 TXN_STATUS=P 或 RETRIEVAL 超时）时，继续下一次轮询；
   * 一旦返回非忽略结果则同步完成主请求并停止轮询。
   */
  poll?: {
    ackReceived: boolean;
    intervalMs: number;
    maxAttempts: number;
    attempts: number;
    timer?: number;
    active?: boolean;
  };
}

/**
 * 响应数据接口
 */
interface ResponseData {
  /** 响应类型 */
  type: "ACK" | "NAK" | "FRAME";
  /** 原始数据 */
  rawData?: Uint8Array;
  /** 解析后的数据 */
  parsedData?: any;
  /** 事件名 */
  eventName?: EventName;
  /** 交易ID */
  txnId?: string;
  /** 时间戳 */
  timestamp: number;
}

/**
 * 事件监听器类型定义
 */
export type EventListenerMap = {
  connectionChange: (event: ConnectionStatusEvent) => void;
  transactionRecord: (event: TransactionRecordEvent) => void;
  transactionComplete: (result: TransactionResult) => void;
  error: (error: Error) => void;
  dataReceived: (data: ResponseData) => void;
};

export const ACK = 0x06;
export const NAK = 0x15;

const DEFAULT_SERIAL_CONFIG: SerialOptions = {
  baudRate: 9600,
  dataBits: 8,
  stopBits: 1,
  parity: "none",
  flowControl: "none",
};

const DEFAULT_MANAGER_CONFIG: Required<Omit<N5PosConfig, "serialConfig">> = {
  defaultTimeout: 120000,
  ackTimeout: 2000,
  frameTimeout: 120000,
  maxRetries: 3,
  debug: false,
  heartbeatInterval: 30000,
};

/**
 * 新版 N5 POS 管理器
 *
 * 根据设计方案重构，采用精细化状态管理和事件驱动架构：
 * 1. 实现完整的连接状态和事务状态机
 * 2. 优化请求队列管理和并发处理
 * 3. 增强错误处理和重试机制
 * 4. 提供统一的事件监听接口
 * 5. 支持心跳检测和连接状态监控
 *
 * @example
 * ```typescript
 * const posManager = new N5PosManagerV2({
 *   debug: true,
 *   defaultTimeout: 60000,
 *   heartbeatInterval: 30000
 * });
 *
 * // 监听连接状态变化
 * posManager.addEventListener('connectionChange', (event) => {
 *   console.log('连接状态:', event.connectionStatus);
 * });
 *
 * await posManager.connect();
 * const result = await posManager.sendTransaction('SALE', '123456', {
 *   TXN_AMT: 100,
 *   PAYMENT_APP_ID: PaymentAppId.ALIPAY
 * });
 * ```
 */
class N5PosManagerV2 {
  // 公共状态 - 根据设计方案优化
  /** 串口连接状态 */
  public connectionStatus = PosConnectionStatus.DISCONNECTED;
  /** POS机健康状态 */
  public healthStatus = PosHealthStatus.UNKNOWN;
  /** 事务处理状态 */
  public transactionState = TransactionState.IDLE;
  /** 交易记录列表 */
  public transactionRecords: TransactionRecord[] = [];

  // 私有属性 - 基础配置
  /** 是否支持Web Serial API */
  #isWebSerialSupported = false;
  /** 串口配置参数 */
  #serialConfig: SerialOptions = DEFAULT_SERIAL_CONFIG;
  /** 串口实例 */
  #serialPort: SerialPort | null = null;
  /** 串口读取器 */
  #reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  /** 串口写入器 */
  #writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  /** 管理器配置 */
  #config: Required<N5PosConfig>;

  // 私有属性 - 事件监听器（根据设计方案优化）
  /** 事件监听器映射 */
  #eventListeners: Map<keyof EventListenerMap, Set<(...args: any[]) => void>> =
    new Map();

  // 私有属性 - 请求管理（根据设计方案优化）
  /** 请求队列，使用Map存储以便快速查找 */
  #requestQueue: Map<string, RequestItem> = new Map();
  /** 当前正在处理的请求 */
  #currentRequest: RequestItem | null = null;
  /** 请求序列号，用于生成唯一的请求ID */
  #requestSequence = 0;
  /** 读取器是否处于活动状态 */
  #isReaderActive = false;
  /** 帧解析器实例 */
  #frameParser: FrameParser | null = null;

  // 私有属性 - 心跳管理
  /** 心跳定时器 */
  #heartbeatTimer: number | null = null;
  /** 心跳检测是否正在进行中 */
  #heartbeatPending = false;

  // 私有属性 - 状态控制
  /** 串口断开监听器的清理函数 */
  #disconnectListenerCleanup: (() => void) | null = null;
  /** 是否正在执行取消操作 */
  #isAborting = false;

  // 私有属性 - 日志上报
  /** 日志上报列表 */
  #logQueue: string[] = [];

  /**
   * 构造函数
   * @param config POS机配置选项
   */
  constructor(config: N5PosConfig = {}) {
    // 合并默认配置和用户配置
    this.#config = {
      ...DEFAULT_MANAGER_CONFIG,
      ...config,
      serialConfig: { ...DEFAULT_SERIAL_CONFIG, ...config.serialConfig },
    };

    // 检查 Web Serial API 支持
    this.#isWebSerialSupported = "serial" in navigator;
    if (!this.#isWebSerialSupported) {
      console.warn("Web Serial API 不受支持");
    }

    // 初始化事件监听器映射
    this.#initializeEventListeners();
  }

  /**
   * 初始化事件监听器映射
   */
  #initializeEventListeners(): void {
    const eventTypes: (keyof EventListenerMap)[] = [
      "connectionChange",
      "transactionRecord",
      "transactionComplete",
      "error",
      "dataReceived",
    ];

    eventTypes.forEach((eventType) => {
      this.#eventListeners.set(eventType, new Set());
    });

    this.#log("事件监听器初始化完成");
  }

  /**
   * 连接串口
   *
   * 执行以下步骤：
   * 1. 检查Web Serial API支持
   * 2. 请求用户选择串口
   * 3. 打开串口连接
   * 4. 初始化读写流
   * 5. 启动持续读取循环
   * 6. 开始心跳检测
   *
   * @throws {Error} 当Web Serial API不支持或连接失败时抛出错误
   */
  async connect(): Promise<void> {
    try {
      if (!this.#isWebSerialSupported) {
        throw new Error("Web Serial API 不受支持");
      }

      this.#setSerialConnectionStatus({
        connectionStatus: PosConnectionStatus.CONNECTING,
        healthStatus: this.healthStatus,
      });

      // 获取上次连接的设备信息，用于自动连接上次使用的设备
      const deviceInfoStr = localStorage.getItem("lastConnectedDevice");
      if (deviceInfoStr) {
        const deviceInfo = JSON.parse(deviceInfoStr);
        const ports = await navigator.serial.getPorts();
        const port =
          ports.find((p) => {
            const info = p.getInfo();
            return (
              info.usbVendorId === deviceInfo.usbVendorId &&
              info.usbProductId === deviceInfo.usbProductId
            );
          }) || null;
        this.#serialPort = port;
      }
      // 如果自动连接不成功，请求用户选择串口
      if (!this.#serialPort) {
        // 请求串口 - 会弹出用户选择对话框
        const port = await navigator.serial.requestPort();
        this.#serialPort = port;
        // 获取当前连接的设备信息，用于后续自动连接
        const deviceInfo = this.#serialPort.getInfo();
        localStorage.setItem("lastConnectedDevice", JSON.stringify(deviceInfo));
      }
      try {
        // 检查串口是否已打开
        if (!this.#serialPort.readable && !this.#serialPort.writable) {
          // 打开端口
          await this.#serialPort.open(this.#config.serialConfig);
        }
      } catch (error) {
        throw "串口被占用";
      }

      // 获取读写流
      this.#reader = this.#serialPort.readable?.getReader() || null;
      this.#writer = this.#serialPort.writable?.getWriter() || null;

      if (!this.#reader || !this.#writer) {
        throw new Error("无法获取串口读写流");
      }

      // 设置断开监听
      this.#setupDisconnectListener();

      // 启动持续读取
      this.#startContinuousReader();

      // 更新连接状态
      this.#setSerialConnectionStatus({
        connectionStatus: PosConnectionStatus.CONNECTED,
        healthStatus: this.healthStatus,
      });
      // 立刻执行一次心跳检测
      await this.#performHeartbeat();
      // 启动心跳
      this.#startHeartbeat();
      // 启动日志上报
      setInterval(() => {
        this.#logQueueUpload();
      }, 5000);

      this.#log("串口连接成功");
    } catch (error) {
      this.#error("串口连接失败:", error);
      this.#setSerialConnectionStatus({
        connectionStatus: PosConnectionStatus.ERROR,
        healthStatus: PosHealthStatus.FAIL,
      });
      throw "串口连接失败:" + error;
    }
  }

  /**
   * 清理串口连接并清除本地存储
   *
   * 执行以下操作：
   * 1. 断开当前串口连接
   * 2. 清除本地存储的设备信息
   * 3. 重置串口实例
   *
   * 使用场景：当用户第一次连接时选择了错误的串口，需要重新选择
   */
  async clearSerialConnection(): Promise<void> {
    try {
      this.#log("开始清理串口连接和本地存储");

      // 1. 断开当前连接（如果已连接）
      if (this.connectionStatus !== PosConnectionStatus.DISCONNECTED) {
        await this.disconnect();
      }

      // 2. 清除本地存储的设备信息
      localStorage.removeItem("lastConnectedDevice");
      this.#log("已清除本地存储的设备信息");

      // 3. 确保串口实例已重置
      this.#serialPort = null;

      this.#log("串口连接和本地存储已清理完成");
    } catch (error) {
      this.#error("清理串口连接时出错:", error);
      throw error;
    }
  }

  /**
   * 断开串口连接
   *
   * 执行清理步骤：
   * 1. 停止心跳检测
   * 2. 停止持续读取循环
   * 3. 清理所有待处理请求
   * 4. 释放读写流资源
   * 5. 关闭串口连接
   * 6. 更新连接状态
   */
  async disconnect(): Promise<void> {
    try {
      this.#log("开始断开串口连接");

      // 设置断开状态
      this.#setSerialConnectionStatus({
        connectionStatus: PosConnectionStatus.DISCONNECTING,
        healthStatus: this.healthStatus,
      });

      // 停止心跳
      this.#stopHeartbeat();

      // 停止持续读取
      this.#stopContinuousReader();

      // 清理请求队列
      this.#clearRequestQueue("串口断开，请求已取消");

      // 清理断开监听器
      if (this.#disconnectListenerCleanup) {
        this.#disconnectListenerCleanup();
        this.#disconnectListenerCleanup = null;
      }

      // 释放读写流
      if (this.#reader) {
        try {
          await this.#reader.cancel();
        } catch (error) {
          this.#log("取消读取器时出错:", error);
        }
        this.#reader.releaseLock();
        this.#reader = null;
      }

      if (this.#writer) {
        try {
          await this.#writer.close();
        } catch (error) {
          this.#log("关闭写入器时出错:", error);
        }
        this.#writer = null;
      }

      // 关闭串口
      if (this.#serialPort) {
        try {
          await this.#serialPort.close();
        } catch (error) {
          this.#log("关闭串口时出错:", error);
        }
        this.#serialPort = null;
      }

      // 重置状态
      this.transactionState = TransactionState.IDLE;
      this.#currentRequest = null;

      // 更新状态
      this.#setSerialConnectionStatus({
        connectionStatus: PosConnectionStatus.DISCONNECTED,
        // 断开后健康状态改为 UNKNOWN，更符合语义
        healthStatus: PosHealthStatus.UNKNOWN,
      });

      this.#log("串口连接已断开");
    } catch (error) {
      this.#error("断开串口连接时出错:", error);
      throw error;
    }
  }

  /**
   * 销售交易
   *
   * @param params 销售交易参数
   * @param params.TXN_ID 交易ID，用于标识唯一交易
   * @param params.TXN_AMT 交易金额（分为单位）
   * @param params.PAYMENT_APP_ID 支付应用ID（支付宝、微信等）
   * @param params.QRC_VALUE 二维码值（可选）
   * @returns 交易结果Promise
   */
  async sale({
    TXN_ID,
    TXN_AMT,
    PAYMENT_APP_ID,
    QRC_VALUE,
  }: {
    TXN_ID: string;
    TXN_AMT: number;
    PAYMENT_APP_ID: PaymentAppId;
    QRC_VALUE?: string;
  }): Promise<TransactionResult<SaleResponse>> {
    // 验证参数
    if (!TXN_ID || TXN_AMT <= 0) {
      throw new Error("无效的交易参数：交易ID和金额必须有效");
    }

    const request: SaleRequest = {
      EVENT_NAME: EventName.SALE,
      TXN_ID,
      TXN_AMT,
      PAYMENT_APP_ID,
      QRC_VALUE,
    };

    return this.#executeTransaction<SaleRequest, SaleResponse>(
      request,
      TXN_AMT
    );
  }

  /**
   * 退款交易
   *
   * @param params 退款交易参数
   * @param params.TXN_ID 交易ID
   * @param params.TXN_AMT 退款金额（分为单位）
   * @param params.PAYMENT_APP_ID 支付应用ID
   * @param params.QRC_VALUE 原交易二维码值（必需）
   * @returns 交易结果Promise
   */
  async refund({
    TXN_ID,
    TXN_AMT,
    PAYMENT_APP_ID,
    QRC_VALUE,
  }: {
    TXN_ID: string;
    TXN_AMT: number;
    PAYMENT_APP_ID: PaymentAppId;
    QRC_VALUE: string;
  }): Promise<TransactionResult<RefundResponse>> {
    // 验证参数
    if (!TXN_ID || TXN_AMT <= 0 || !QRC_VALUE) {
      throw new Error("无效的退款参数：交易ID、金额和二维码值必须有效");
    }

    const request: RefundRequest = {
      EVENT_NAME: EventName.REFUND,
      TXN_ID,
      TXN_AMT,
      PAYMENT_APP_ID,
      QRC_VALUE,
    };

    return this.#executeTransaction<RefundRequest, RefundResponse>(
      request,
      TXN_AMT
    );
  }

  /**
   * 作废交易
   *
   * @param params 作废交易参数
   * @param params.TXN_ID 原交易ID
   * @returns 交易结果Promise
   */
  async void({
    TXN_ID,
    PAYMENT_APP_ID,
  }: {
    TXN_ID: string;
    PAYMENT_APP_ID: PaymentAppId;
  }): Promise<TransactionResult<VoidResponse>> {
    // 验证参数
    if (!TXN_ID) {
      throw new Error("无效的作废参数：交易ID必须有效");
    }

    const request: VoidRequest = {
      EVENT_NAME: EventName.VOID,
      TXN_ID,
      PAYMENT_APP_ID,
    };

    return this.#executeTransaction<VoidRequest, VoidResponse>(request);
  }

  /**
   * 中止当前交易
   *
   * 执行以下操作：
   * 1. 设置中止标志，防止新的交易请求
   * 2. 清理所有待处理的请求队列
   * 3. 发送中止命令到POS机
   * 4. 等待POS机确认中止操作
   *
   * @returns 中止操作结果
   */
  async abort(): Promise<FrameResult<AbortResponse>> {
    // 未连接 直接关闭成功。不发送POS机请求
    if (!this.#serialPort) {
      return {
        status: "COMPLETE",
        msg: "操作成功",
      };
    }
    this.#log("开始中止操作");
    this.transactionState = TransactionState.ABORTING;

    try {
      const request: AbortRequest = {
        EVENT_NAME: EventName.ABORT,
      };

      // 清理所有待处理的请求
      this.#clearRequestQueue("交易已被中止");

      // 发送中止请求 - 使用特殊处理因为AbortResponse没有TXN_ID
      const result = await this.#sendAbortRequest(request);

      // 转换为FrameResult格式以保持API兼容性
      return {
        status: result.status,
        data: result.data,
        msg: result.msg || (result.success ? "操作成功" : "操作失败"),
        code: result.success ? 0 : 1,
      } as FrameResult<AbortResponse>;
    } finally {
      this.transactionState = TransactionState.IDLE;
      this.#log("中止操作完成");
    }
  }

  /**
   * 清机结算
   * @returns 结算结果Promise
   */
  async settleMent(): Promise<FrameResult<SettlementResponse>> {
    const request: SettlementRequest = {
      EVENT_NAME: EventName.SETTLE,
    };

    const result = await this.#sendRequest<SettlementResponse>(request);
    return {
      status: result.status,
      data: result.data,
      msg: result.msg || (result.success ? "结算成功" : "结算失败"),
      code: result.success ? 0 : 1,
    };
  }

  /**
   * 查询POS机健康状态
   */
  async checkHealth(): Promise<FrameResult<HealthCheckResponse>> {
    const request: HealthCheckRequest = {
      EVENT_NAME: EventName.HEALTH_CHECK,
    };

    const result = await this.#sendRequest<HealthCheckResponse>(request, {
      timeout: 5000,
      maxRetries: 0,
    });
    if (result.success) {
      this.#setSerialConnectionStatus({
        connectionStatus: PosConnectionStatus.CONNECTED,
        healthStatus: PosHealthStatus.SUCCESS,
      });
    } else {
      this.#setSerialConnectionStatus({
        connectionStatus: this.connectionStatus,
        healthStatus: PosHealthStatus.FAIL,
      });
    }
    // 转换为FrameResult格式以保持API兼容性
    return {
      status: result.status,
      data: result.data,
      msg: result.msg || (result.success ? "健康检查成功" : "健康检查失败"),
      code: result.success ? 0 : 1,
    };
  }

  /**
   * 心跳检测
   *
   * 发送ECHO命令检测POS机连接状态和响应能力
   *
   * @returns 心跳检测结果
   */
  async checkHeartbeat(): Promise<FrameResult<LinkTestResponse>> {
    const request: LinkTestRequest = {
      EVENT_NAME: EventName.ECHO,
    };

    const result = await this.#sendRequest<LinkTestResponse>(request, {
      timeout: this.#config.ackTimeout,
      maxRetries: 0,
    });

    // 转换为FrameResult格式以保持API兼容性
    return {
      status: result.status,
      data: result.data,
      msg: result.msg || (result.success ? "心跳检测成功" : "心跳检测失败"),
      code: result.success ? 0 : 1,
    } as FrameResult<LinkTestResponse>;
  }

  // ==================== 新的统一API方法 ====================

  /**
   * 发送交易请求（统一接口）
   *
   * @param eventName 事件名称
   * @param txnId 交易ID
   * @param data 交易数据
   * @returns 交易结果
   */
  async sendTransaction<T extends BaseResponse = BaseResponse>(
    eventName: EventName,
    txnId: string,
    data: any
  ): Promise<TransactionResult<T>> {
    const request = {
      EVENT_NAME: eventName,
      TXN_ID: txnId,
      ...data,
    } as BaseRequest;

    return this.#executeTransaction<BaseRequest, T>(request, data.TXN_AMT);
  }

  /**
   * 发送心跳请求
   *
   * @returns 心跳结果
   */
  async sendEcho(): Promise<TransactionResult<LinkTestResponse>> {
    const request: LinkTestRequest = {
      EVENT_NAME: EventName.ECHO,
    };

    return this.#sendRequest<LinkTestResponse>(request, {
      timeout: this.#config.ackTimeout,
      maxRetries: 0,
    });
  }

  /**
   * 发送取消请求
   *
   * @param txnId 要取消的交易ID
   * @returns 取消结果
   */
  async sendAbort(txnId?: string): Promise<TransactionResult<AbortResponse>> {
    const request: AbortRequest = {
      EVENT_NAME: EventName.ABORT,
    };

    // 清理所有待处理的请求
    this.#clearRequestQueue("交易已被中止");

    return this.#sendAbortRequest(request);
  }

  /**
   * 发送查找请求
   *
   * @param txnId 要查找的交易ID
   * @returns 查找结果
   */
  async sendRetrieval(txnId: string): Promise<TransactionResult<BaseResponse>> {
    const request = {
      EVENT_NAME: EventName.RETRIEVAL,
      TXN_ID: txnId,
    } as BaseRequest;
    this.#log("手动查找交易", JSON.stringify(request));
    this.transactionState = TransactionState.RETRIEVING;

    try {
      return await this.#sendRequest<BaseResponse>(request);
    } finally {
      this.transactionState = TransactionState.IDLE;
    }
  }

  // ==================== 事件监听器管理方法 ====================

  /**
   * 添加事件监听器
   *
   * @param eventType 事件类型
   * @param listener 监听器函数
   * @returns 移除监听器的函数
   */
  addEventListener<K extends keyof EventListenerMap>(
    eventType: K,
    listener: EventListenerMap[K]
  ): () => void {
    const listeners = this.#eventListeners.get(eventType);
    if (listeners) {
      listeners.add(listener);
    }

    return () => {
      this.removeEventListener(eventType, listener);
    };
  }

  /**
   * 移除事件监听器
   *
   * @param eventType 事件类型
   * @param listener 要移除的监听器函数
   */
  removeEventListener<K extends keyof EventListenerMap>(
    eventType: K,
    listener: EventListenerMap[K]
  ): void {
    const listeners = this.#eventListeners.get(eventType);
    if (listeners) {
      listeners.delete(listener);
    }
  }

  /**
   * 触发事件
   *
   * @param eventType 事件类型
   * @param data 事件数据
   */
  #emitEvent<K extends keyof EventListenerMap>(eventType: K, data: any): void {
    const listeners = this.#eventListeners.get(eventType);
    if (listeners) {
      listeners.forEach((listener) => {
        try {
          (listener as any)(data);
        } catch (error) {
          this.#error(`事件监听器执行错误 [${eventType}]:`, error);
        }
      });
    }
  }

  // ==================== 兼容性方法（保持向后兼容） ====================

  /**
   * 添加串口状态监听器（兼容性方法）
   */
  addSerialStatusListener(
    listener: (status: {
      connectionStatus: PosConnectionStatus;
      healthStatus: PosHealthStatus;
    }) => void
  ): () => void {
    return this.addEventListener("connectionChange", (event) => {
      listener({
        connectionStatus: event.connectionStatus,
        healthStatus: event.healthStatus,
      });
    });
  }

  /**
   * 移除串口状态监听器（兼容性方法）
   */
  removeSerialStatusListener(
    listener: (status: {
      connectionStatus: PosConnectionStatus;
      healthStatus: PosHealthStatus;
    }) => void
  ): void {
    // 由于包装了监听器，这里只能清空所有连接状态监听器
    const listeners = this.#eventListeners.get("connectionChange");
    if (listeners) {
      listeners.clear();
    }
  }

  /**
   * 添加交易记录监听器（兼容性方法）
   */
  addTransactionRecordListener(
    listener: (event: TransactionRecordEvent) => void
  ): () => void {
    return this.addEventListener("transactionRecord", listener);
  }

  /**
   * 移除交易记录监听器（兼容性方法）
   */
  removeTransactionRecordListener(
    listener: (event: TransactionRecordEvent) => void
  ): void {
    this.removeEventListener("transactionRecord", listener);
  }

  // ==================== 交易记录管理方法 ====================

  /**
   * 获取所有交易记录的副本
   *
   * @returns 交易记录数组的副本
   */
  getTransactionRecords(): TransactionRecord[] {
    return [...this.transactionRecords];
  }

  /**
   * 清空所有交易记录
   */
  clearTransactionRecords(): void {
    this.transactionRecords = [];
    this.#notifyTransactionRecordListeners({
      type: "cleared",
      records: [],
    });
  }

  /**
   * 获取最后一条交易记录
   *
   * @returns 最后一条交易记录，如果没有则返回null
   */
  getLastTransaction(): TransactionRecord | null {
    return this.transactionRecords.length > 0
      ? this.transactionRecords[this.transactionRecords.length - 1]
      : null;
  }

  /**
   * 更新指定交易记录
   *
   * @param eventName_txnId EVENTNAME + 交易ID
   * @param partial 要更新的字段
   * @returns 是否更新成功
   */
  updateTransactionRecord(
    eventName_txnId: string,
    partial: Partial<TransactionRecord>
  ): boolean {
    const index = this.transactionRecords.findIndex(
      (record) => `${record.eventName}_${record.txnId}` === eventName_txnId
    );
    if (index !== -1) {
      const oldRecord = this.transactionRecords[index];
      this.transactionRecords[index] = { ...oldRecord, ...partial };
      this.#notifyTransactionRecordListeners({
        type: "updated",
        record: this.transactionRecords[index],
        records: this.transactionRecords,
      });
      return true;
    }
    return false;
  }

  // ==================== 私有方法实现 ====================

  /**
   * 启动持续读取循环
   *
   * 初始化帧解析器并启动异步读取循环，
   * 持续监听串口数据直到连接断开
   */
  #startContinuousReader(): void {
    if (this.#isReaderActive) {
      return;
    }

    this.#isReaderActive = true;
    this.#frameParser = new FrameParser();
    this.#log("启动持续读取循环");

    // 启动异步读取循环
    this.#continuousReadLoop().catch((error) => {
      this.#error("持续读取循环出错:", error);
      this.#handleReaderError(error);
    });
  }

  /**
   * 停止持续读取循环
   *
   * 设置停止标志并清理帧解析器
   */
  #stopContinuousReader(): void {
    this.#isReaderActive = false;
    this.#frameParser = null;
    this.#log("停止持续读取循环");
  }

  /**
   * 持续读取循环
   *
   * 在独立的异步循环中持续读取串口数据，
   * 直到读取器被停止或发生错误
   */
  async #continuousReadLoop(): Promise<void> {
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 5;
    const baseRetryDelay = 100;
    const maxRetryDelay = 5000;

    while (this.#isReaderActive && this.#reader) {
      try {
        // 持续读取数据,不设置超时,由请求级别的超时机制处理响应超时
        const { value, done } = await this.#reader.read();

        if (done) {
          this.#log("读取流已结束");
          break;
        }

        if (!value || value.length === 0) {
          continue;
        }

        // 记录接收到的原始数据（调试用）
        // if (this.#config.debug) {
        //   const hexString = Array.from(value, (byte) =>
        //     byte.toString(16).padStart(2, "0")
        //   ).join(" ");
        //   this.#log("接收到数据:", hexString);
        // }

        // 处理接收到的数据
        await this.#processReceivedData(value);
        consecutiveErrors = 0; // 重置错误计数
      } catch (error) {
        if (!this.#isReaderActive) {
          break;
        }

        consecutiveErrors++;
        this.#error(
          `读取数据时出错 (${consecutiveErrors}/${maxConsecutiveErrors}):`,
          error
        );

        // 如果连续错误过多，停止读取
        if (consecutiveErrors >= maxConsecutiveErrors) {
          this.#error("连续错误过多，停止读取循环");
          this.#handleReaderError(error);
          break;
        }

        // 计算重试延迟（指数退避）
        const retryDelay = Math.min(
          baseRetryDelay * Math.pow(2, consecutiveErrors - 1),
          maxRetryDelay
        );

        this.#log(`等待 ${retryDelay}ms 后重试`);
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
      }
    }

    this.#log("连续读取循环已停止");
  }

  /**
   * 处理接收到的数据
   *
   * 使用帧解析器解析原始数据，根据解析结果：
   * 1. 对于完整帧：发送ACK确认并分发响应
   * 2. 对于ACK/NAK：直接分发响应
   * 3. 对于不完整帧：继续等待更多数据
   *
   * @param data 接收到的原始数据
   */
  async #processReceivedData(data: Uint8Array): Promise<void> {
    if (!this.#frameParser) {
      this.#log("帧解析器未初始化，跳过数据处理");
      return;
    }

    try {
      const results = await this.#frameParser.parseChunk(data);
      if (!results || results.length === 0) {
        return;
      }
      for (const result of results) {
        const responseData: ResponseData = {
          type:
            result.status === "ACK"
              ? "ACK"
              : result.status === "NAK"
              ? "NAK"
              : "FRAME",
          rawData: data,
          timestamp: Date.now(),
        };
        if (result.status === "COMPLETE" && result.data) {
          responseData.parsedData = result.data;
          if (result.data && typeof result.data === "object") {
            const eventName = (result.data as any).EVENT_NAME;
            const txnId = (result.data as any).TXN_ID;
            if (eventName) {
              responseData.eventName = eventName;
              responseData.txnId = txnId;
              this.#log(`收到完整帧: ${JSON.stringify(result.data)}`);
            } else {
              this.#log("收到完整帧但缺少事件名称");
            }
          }
          try {
            await this.#sendAck();
          } catch (ackError) {
            this.#error("发送ACK确认失败:", ackError);
          }
        }
        this.#emitEvent("dataReceived", responseData);
        if (result.status === "ACK" || result.status === "COMPLETE") {
          this.#dispatchResponse(responseData);
        } else if (result.status === "NAK") {
          this.#log("收到NAK响应");
          this.#dispatchResponse(responseData);
          // eslint-disable-next-line no-empty
        } else if (result.status === "INCOMPLETE") {
        } else {
          this.#log(`收到未知状态的响应: ${result.status}`);
        }
      }
    } catch (error) {
      this.#error("处理接收数据时出错:", error);

      // 发送错误事件
      this.#emitEvent("error", new Error(`数据处理失败: ${error}`));
    }
  }

  /**
   * 分发响应到对应的请求
   *
   * 根据响应类型和内容，将响应匹配到相应的待处理请求：
   * 1. ACK/NAK响应：匹配最近发送的请求
   * 2. 完整帧响应：通过事件名和交易ID精确匹配
   *
   * @param responseData 响应数据对象
   */
  #dispatchResponse(responseData: ResponseData): void {
    this.#log(
      "分发响应:",
      responseData.type,
      responseData.eventName,
      responseData.txnId
    );

    // 处理ACK/NAK响应
    if (responseData.type === "ACK" || responseData.type === "NAK") {
      this.#handleControlResponse(responseData);
      return;
    }

    // 处理完整帧响应
    if (responseData.type === "FRAME" && responseData.eventName) {
      this.#handleFrameResponse(responseData);
      return;
    }

    this.#log("无法匹配响应到任何请求");
  }

  /**
   * 处理控制响应（ACK/NAK）
   *
   * ACK表示POS机已收到请求，NAK表示请求格式错误需要重试
   *
   * @param responseData 控制响应数据
   */
  #handleControlResponse(responseData: ResponseData): void {
    // 优先使用当前在途请求
    let pendingRequest = this.#currentRequest;
    if (!pendingRequest) {
      // 回退：查找最近发送的请求（按时间戳倒序）
      pendingRequest = Array.from(this.#requestQueue.values())
        .filter((req) => req.status === "sent")
        .sort((a, b) => b.timestamp - a.timestamp)[0];
    }

    if (!pendingRequest) {
      this.#log("没有找到等待ACK/NAK的请求");
      return;
    }

    if (responseData.type === "ACK") {
      this.#log(`请求 ${pendingRequest.id} 收到ACK`);
      // 清除 ACK 定时器
      if (pendingRequest.ackTimeoutTimer) {
        clearTimeout(pendingRequest.ackTimeoutTimer);
        pendingRequest.ackTimeoutTimer = undefined;
      }
      pendingRequest.status = "ack_received";
      // 更新事务状态为等待最终响应
      this.transactionState = TransactionState.WAITING_FOR_RESPONSE;

      // 更新交易记录（若有 TXN_ID）
      const baseReq = pendingRequest.request as BaseRequest;
      const txnId = baseReq.TXN_ID;
      if (txnId) {
        this.updateTransactionRecord(`${baseReq.EVENT_NAME}_${txnId}`, {
          requestStatus: "ack_received",
        });
      }
      // ACK 路由到轮询策略：
      // - 首次 ACK：记录 ackReceived 并启动轮询；
      // - 后续 ACK：仅唤醒/同步轮询策略（如尚未激活则启动）。
      if (pendingRequest.poll) {
        if (!pendingRequest.poll.ackReceived) {
          pendingRequest.poll.ackReceived = true;
          window.setTimeout(() => {
            this.#startPollStrategy(
              pendingRequest as RequestItem<BaseResponse | AbortResponse>
            );
          }, 15000);
        } else {
          this.#notifyPollAck(pendingRequest);
        }
      }
    } else if (responseData.type === "NAK") {
      this.#log(`请求 ${pendingRequest.id} 收到NAK，准备重试`);
      this.#handleRequestRetry(pendingRequest, "NAK响应");
    }
  }

  /**
   * 处理帧响应
   *
   * 根据事件名和交易ID匹配对应的请求，并完成该请求
   *
   * @param responseData 帧响应数据
   */
  #handleFrameResponse(responseData: ResponseData): void {
    const { eventName, txnId, parsedData } = responseData;

    if (!eventName) {
      this.#log("响应中缺少 EVENT_NAME");
      return;
    }

    // 查找对应的请求 - 对于没有TXN_ID的响应，txnId可能为undefined
    const matchingRequest = this.#findMatchingRequest(eventName, txnId);

    if (!matchingRequest) {
      this.#log(`没有找到匹配的请求: ${eventName} ${txnId || "(无TXN_ID)"}`);
      return;
    }

    this.#log(
      `请求 ${matchingRequest.id} 收到完整响应 状态：${parsedData?.STATUS}`
    );

    // 根据返回的 STATUS 判定交易是否成功（仅 STATUS === "00" || "02" 和 TXN_STATUS === "A" 视为成功）
    const isSuccess = parsedData.TXN_STATUS
      ? parsedData?.STATUS === StatusCode.APPROVED &&
        parsedData.TXN_STATUS === TransactionStatus.APPROVED
      : parsedData?.STATUS === StatusCode.APPROVED ||
        parsedData.STATUS === StatusCode.REFUND_PROCESSING;

    // 完成请求，success 仅在 STATUS 为 00 时为 true，其余情况为 false
    this.#completeRequest(matchingRequest, {
      success: isSuccess,
      status: "COMPLETE",
      data: parsedData,
      msg: `${POS_STATUS_DESC[parsedData?.STATUS] || "交易失败"}: ${
        RESP_CODE_DESC[parsedData.RESP_CODE] || ""
      } ${parsedData.RESP_CODE || ""}`,
      statusCode: parsedData?.STATUS,
    });

    // 对 ECR_BUSY(97) 的处理：
    // - 非 RETRIEVAL_RESP：认为当前设备不在首页/繁忙，触发中止以尽快回收队列；
    // - RETRIEVAL_RESP：属于轮询查询的忽略条件，交由轮询策略继续下一次查询，不中止。
    if (
      parsedData?.STATUS === StatusCode.ECR_BUSY &&
      eventName !== EventName.RETRIEVAL_RESP
    ) {
      this.abort();
    }
  }

  /**
   * 查找匹配的请求
   *
   * 匹配策略：
   * 1. 对于有TXN_ID的响应：精确匹配TXN_ID和事件名
   * 2. 对于无TXN_ID的响应：匹配事件名并选择最近的请求
   *
   * @param eventName 响应事件名
   * @param txnId 交易ID（可选）
   * @returns 匹配的请求项，如果没有找到则返回null
   */
  #findMatchingRequest(
    eventName: EventName,
    txnId?: string
  ): RequestItem | null {
    // 对于没有TXN_ID的响应（如abort、echo），直接通过事件名匹配
    if (!txnId) {
      const candidates = Array.from(this.#requestQueue.values())
        .filter((req) =>
          this.#isResponseForRequest(eventName, req.request.EVENT_NAME)
        )
        .sort((a, b) => b.timestamp - a.timestamp);

      return candidates[0] || null;
    }

    // 对于有TXN_ID的响应，首先尝试精确匹配
    for (const request of this.#requestQueue.values()) {
      // 处理其他有TXN_ID的请求
      const requestWithTxnId = request.request as BaseRequest;
      if (
        requestWithTxnId.TXN_ID === txnId &&
        this.#isResponseForRequest(eventName, request.request.EVENT_NAME)
      ) {
        return request;
      }
    }

    // 响应包含 TXN_ID 但未找到匹配请求时，不做事件名回退，避免误把旧响应匹配到当前请求
    return null;
  }

  /**
   * 判断响应是否对应请求
   *
   * 根据预定义的事件映射关系判断响应事件是否对应请求事件
   *
   * @param responseEvent 响应事件名
   * @param requestEvent 请求事件名
   * @returns 是否匹配
   */
  #isResponseForRequest(
    responseEvent: EventName,
    requestEvent: EventName
  ): boolean {
    return responseEvent === `${requestEvent}_RESP`;
  }

  /**
   * 执行交易
   *
   * 通用的交易执行方法，处理交易记录的创建和更新
   *
   * @param request 交易请求
   * @param amount 交易金额（可选）
   * @returns 交易结果
   */
  async #executeTransaction<
    TRequest extends BaseRequest,
    TResponse extends BaseResponse
  >(request: TRequest, amount?: number): Promise<TransactionResult<TResponse>> {
    try {
      // 检查是否正在中止操作
      if (this.#isAborting) {
        return {
          success: false,
          msg: "正在执行中止操作，无法处理新的交易请求",
          status: "ERROR",
        };
      }

      // 创建交易记录
      const transactionRecord: TransactionRecord = {
        txnId: request.TXN_ID,
        eventName: request.EVENT_NAME,
        amount,
        timestamp: Date.now(),
        requestStatus: "pending",
      };

      // 添加到交易记录列表
      this.transactionRecords.push(transactionRecord);
      this.#notifyTransactionRecordListeners({
        type: "added",
        record: transactionRecord,
        records: this.transactionRecords,
      });
      // 更新交易状态为已发送
      this.updateTransactionRecord(`${request.EVENT_NAME}_${request.TXN_ID}`, {
        requestStatus: "sent",
      });

      // 发送请求
      const result = await this.#sendRequest<TResponse>(request);

      // 更新交易状态
      this.updateTransactionRecord(`${request.EVENT_NAME}_${request.TXN_ID}`, {
        requestStatus: result.success ? "completed" : "failed",
        status: result.data?.STATUS,
        errorMessage: result.msg,
      });

      return result;
    } catch (error) {
      // 更新交易状态为失败
      this.updateTransactionRecord(`${request.EVENT_NAME}_${request.TXN_ID}`, {
        requestStatus: "failed",
        errorMessage: error instanceof Error ? error.message : String(error),
      });

      return {
        success: false,
        msg: error instanceof Error ? error.message : String(error),
        status: "ERROR",
      };
    }
  }

  /**
   * 发送请求
   *
   * 创建请求项并添加到队列，然后立即发送到设备
   *
   * @param request 请求对象
   * @param options 请求选项（超时时间、重试次数等）
   * @returns 交易结果Promise
   */
  async #sendRequest<T extends BaseResponse>(
    request: BaseRequest,
    options: {
      timeout?: number;
      maxRetries?: number;
    } = {}
  ): Promise<TransactionResult<T>> {
    // 验证连接状态
    await this.#validateConnection();

    const {
      timeout = this.#config.defaultTimeout,
      maxRetries = this.#config.maxRetries,
    } = options;

    return new Promise<TransactionResult<T>>((resolve, reject) => {
      const requestId = this.#generateRequestId(
        request.EVENT_NAME,
        request.TXN_ID
      );

      const requestItem: RequestItem<T> = {
        id: requestId,
        request,
        timestamp: Date.now(),
        timeout,
        retryCount: 0,
        maxRetries,
        status: "pending",
        resolve,
        reject,
      };

      // 当允许重试（maxRetries > 0）时，为该请求初始化轮询策略；
      // 轮询仅在收到 ACK 后启动，用于在主请求等待最终响应期间主动查询交易状态。
      if (maxRetries > 0) {
        requestItem.poll = {
          ackReceived: false,
          intervalMs: 5000,
          maxAttempts: 12,
          attempts: 0,
          active: false,
        };
      }

      // 添加到请求队列
      this.#requestQueue.set(requestId, requestItem as RequestItem);
      // 处理队列中的下一个请求（串行化）
      this.#processNextRequest();
    });
  }

  /**
   * 发送中止请求（特殊处理，因为AbortResponse没有TXN_ID）
   *
   * @param request 中止请求对象
   * @returns 中止操作结果Promise
   */
  async #sendAbortRequest(
    request: AbortRequest
  ): Promise<TransactionResult<AbortResponse>> {
    return new Promise<TransactionResult<AbortResponse>>((resolve, reject) => {
      const requestId = this.#generateRequestId(request.EVENT_NAME);

      const requestItem: RequestItem<AbortResponse> = {
        id: requestId,
        request: request as any, // 类型转换，因为AbortRequest不完全符合BaseRequest
        timestamp: Date.now(),
        timeout: 5000,
        retryCount: 0,
        maxRetries: 1,
        status: "pending",
        priority: "high",
        resolve,
        reject,
      };

      // 添加到请求队列
      this.#requestQueue.set(requestId, requestItem as RequestItem);
      // ABORT 绕过队列，立即发送（最高优先级）
      this.#sendRequestToDevice(requestItem as RequestItem, true);
    });
  }

  /**
   * 直发 RETRIEVAL 查询（不走队列）
   *
   * 用途：作为轮询策略的单次查询动作，绕过队列直接下发到设备，降低等待时间；
   * 行为：
   * - timeout 固定为 4000ms
   * - maxRetries 固定为 0（不重试）
   * - priority 设为 'high' 并使用 force=true 旁路队列
   */
  async #sendRetrievalImmediate(
    txnId: string,
    sourceRequestId: string
  ): Promise<TransactionResult<RetrievalResponse>> {
    return new Promise<TransactionResult<RetrievalResponse>>(
      (resolve, reject) => {
        const requestId = this.#generateRequestId(EventName.RETRIEVAL, txnId);
        const request = {
          EVENT_NAME: EventName.RETRIEVAL,
          TXN_ID: txnId,
        } as BaseRequest;

        const requestItem: RequestItem<RetrievalResponse> = {
          id: requestId,
          request,
          timestamp: Date.now(),
          timeout: 4000,
          retryCount: 0,
          maxRetries: 0,
          status: "pending",
          priority: "high",
          resolve,
          reject,
        };

        this.#requestQueue.set(requestId, requestItem as RequestItem);
        this.#sendRequestToDevice(requestItem as RequestItem, true);
      }
    );
  }

  /**
   * 处理请求队列（串行化）
   * - 仅当当前没有在途请求时，选择下一个待发送的请求
   * - 高优先级优先，先进先出
   */
  #processNextRequest(): void {
    if (this.#currentRequest) return;
    const pending = Array.from(this.#requestQueue.values()).filter(
      (r) => r.status === "pending"
    );
    if (pending.length === 0) return;
    const high = pending
      .filter((r) => r.priority === "high")
      .sort((a, b) => a.timestamp - b.timestamp);
    const normal = pending
      .filter((r) => r.priority !== "high")
      .sort((a, b) => a.timestamp - b.timestamp);
    const next = high[0] ?? normal[0];
    if (next) this.#sendRequestToDevice(next);
  }

  /**
   * 发送请求到设备
   *
   * 将请求序列化为帧格式并通过串口发送到POS机
   *
   * @param requestItem 请求项
   */
  async #sendRequestToDevice(
    requestItem: RequestItem,
    force = false
  ): Promise<void> {
    try {
      await this.#validateConnection();

      // 串行门控：仅允许一个在途请求；ABORT 可强制绕过
      if (
        !force &&
        this.#currentRequest &&
        this.#currentRequest !== requestItem
      ) {
        return;
      }

      // 标记当前请求并暂停心跳
      this.#currentRequest = requestItem;
      this.#stopHeartbeat();

      const requestStr = JSON.stringify(requestItem.request);
      // 创建请求帧
      const frame = createReqFromMsg(requestStr);
      this.#log(`发送请求 ${requestItem.id}:`, requestStr);

      // 发送到设备
      await this.#writer?.write(frame);

      // 更新状态
      requestItem.status = "sent";
      requestItem.timestamp = Date.now();

      // 设置 ACK 超时定时器（等待握手 2s）
      if (requestItem.ackTimeoutTimer) {
        clearTimeout(requestItem.ackTimeoutTimer);
      }
      requestItem.ackTimeoutTimer = window.setTimeout(() => {
        this.#handleAckTimeout(requestItem);
      }, this.#config.ackTimeout);

      // 设置最终响应超时定时器（例如 120s）
      if (requestItem.timeoutTimer) {
        clearTimeout(requestItem.timeoutTimer);
      }
      requestItem.timeoutTimer = window.setTimeout(() => {
        this.#handleRequestTimeout(requestItem);
      }, requestItem.timeout);

      // 更新状态机
      this.transactionState = TransactionState.WAITING_FOR_ACK;
    } catch (error) {
      this.#error(`发送请求 ${requestItem.id} 失败:`, error);
      this.#completeRequest(requestItem, {
        success: false,
        status: "ERROR",
        msg: error instanceof Error ? error.message : "发送失败",
      });
    }
  }

  /**
   * 处理 ACK 超时
   * 当在 ackTimeout 时间内未收到 ACK 时，根据重试策略重发或失败
   */
  #handleAckTimeout(requestItem: RequestItem): void {
    // 仅处理当前在途请求的 ACK 超时
    if (this.#currentRequest && this.#currentRequest.id !== requestItem.id) {
      return;
    }
    this.#log(`请求 ${requestItem.id} 等待ACK超时`);
    if (requestItem.retryCount < requestItem.maxRetries) {
      this.#handleRequestRetry(requestItem, "ACK超时");
    } else {
      this.#completeRequest(requestItem, {
        success: false,
        status: "TIMEOUT",
        msg: "ACK超时",
      });
    }
  }

  /**
   * 处理请求超时
   *
   * 当请求在指定时间内没有收到响应时触发
   *
   * @param requestItem 超时的请求项
   */
  #handleRequestTimeout(requestItem: RequestItem): void {
    this.#log(`请求 ${requestItem.id} 超时`);

    // 判断是否已收到ACK
    if (requestItem.status === "ack_received") {
      // 已收到ACK,说明POS机正在处理,不应该重试
      // 而是应该发送RETRIEVAL查询交易结果
      this.#log(`请求 ${requestItem.id} 已收到ACK`);

      // 清除定时器
      if (requestItem.ackTimeoutTimer) {
        clearTimeout(requestItem.ackTimeoutTimer);
        requestItem.ackTimeoutTimer = undefined;
      }

      // 释放当前在途请求
      if (this.#currentRequest && this.#currentRequest.id === requestItem.id) {
        this.#currentRequest = null;
        this.transactionState = TransactionState.IDLE;
      }

      // 推进队列
      this.#processNextRequest();

      // 完成请求(超时状态)
      this.#completeRequest(requestItem, {
        success: false,
        status: "TIMEOUT",
        msg: "POS机响应超时",
      });
      // 主请求超时时停止轮询，仅清理轮询策略，不改变轮询对主请求的忽略语义
      if (requestItem.poll?.active) {
        this.#stopPollStrategy(requestItem);
      }
    } else {
      // 未收到ACK,可能是发送失败,应该重试
      if (requestItem.retryCount < requestItem.maxRetries) {
        this.#handleRequestRetry(requestItem, "超时");
      } else {
        // 重试次数用尽
        if (requestItem.ackTimeoutTimer) {
          clearTimeout(requestItem.ackTimeoutTimer);
          requestItem.ackTimeoutTimer = undefined;
        }

        if (
          this.#currentRequest &&
          this.#currentRequest.id === requestItem.id
        ) {
          this.#currentRequest = null;
          this.transactionState = TransactionState.IDLE;
        }

        this.#processNextRequest();

        this.#completeRequest(requestItem, {
          success: false,
          status: "TIMEOUT",
          msg: "请求超时",
        });
        // 主请求因未收到 ACK 而超时：同样停止轮询，保持队列推进
        if (requestItem.poll?.active) {
          this.#stopPollStrategy(requestItem);
        }
      }
    }
  }

  /**
   * 处理请求重试
   *
   * 在请求失败或超时时进行重试
   *
   * @param requestItem 需要重试的请求项
   * @param reason 重试原因
   */
  #handleRequestRetry(requestItem: RequestItem, reason: string): void {
    if (requestItem.retryCount >= requestItem.maxRetries) {
      this.#log(`请求 ${requestItem.id} 重试次数达到上限`);
      return;
    }

    requestItem.retryCount++;
    requestItem.status = "pending";

    this.#log(
      `请求 ${requestItem.id} 重试 (${requestItem.retryCount}/${requestItem.maxRetries}): ${reason}`
    );

    // 清除旧的超时定时器
    if (requestItem.timeoutTimer) {
      clearTimeout(requestItem.timeoutTimer);
    }
    if (requestItem.ackTimeoutTimer) {
      clearTimeout(requestItem.ackTimeoutTimer);
      requestItem.ackTimeoutTimer = undefined;
    }

    // 释放当前在途请求以允许重试或其他请求处理
    if (this.#currentRequest && this.#currentRequest.id === requestItem.id) {
      this.#currentRequest = null;
      this.transactionState = TransactionState.IDLE;
    }

    // 延迟2秒后重新发送
    setTimeout(() => {
      this.#sendRequestToDevice(requestItem);
    }, 2000);
  }

  /**
   * 完成请求
   *
   * 清理请求相关资源并调用回调函数
   *
   * @param requestItem 要完成的请求项
   * @param result 请求结果
   */
  #completeRequest<T extends BaseResponse>(
    requestItem: RequestItem<T>,
    result: TransactionResult<T>
  ): void {
    // 清除定时器
    if (requestItem.timeoutTimer) {
      clearTimeout(requestItem.timeoutTimer);
      requestItem.timeoutTimer = undefined;
    }
    if (requestItem.ackTimeoutTimer) {
      clearTimeout(requestItem.ackTimeoutTimer);
      requestItem.ackTimeoutTimer = undefined;
    }

    // 释放当前在途请求并恢复状态/心跳
    if (this.#currentRequest && this.#currentRequest.id === requestItem.id) {
      this.#currentRequest = null;
      this.transactionState = TransactionState.IDLE;
    }

    // 从队列中移除
    this.#requestQueue.delete(requestItem.id);

    // 更新状态
    requestItem.status = result.success ? "completed" : "failed";

    // 更新交易记录（若有 TXN_ID）
    const baseReq = requestItem.request as BaseRequest;
    if (baseReq?.TXN_ID) {
      this.updateTransactionRecord(`${baseReq.EVENT_NAME}_${baseReq.TXN_ID}`, {
        requestStatus: requestItem.status,
        retryCount: requestItem.retryCount,
        responseData: result.data,
        errorMessage: result.msg,
      });
    }

    this.#log(`请求 ${requestItem.id} 完成:`, result.status);

    // 若该请求启用了轮询策略，完成时需要停止轮询并清理定时器，避免资源泄漏或重复执行
    const isRetrieval = baseReq.EVENT_NAME === EventName.RETRIEVAL;
    if (requestItem.poll && !isRetrieval) {
      this.#stopPollStrategy(
        requestItem as RequestItem<BaseResponse | AbortResponse>
      );
    }

    // 触发完成事件
    this.#emitEvent("transactionComplete", result);

    // 恢复心跳，并推动队列继续处理
    this.#startHeartbeat();
    this.#processNextRequest();

    // 调用回调
    requestItem.resolve(result);
  }

  /**
   * 发送ACK确认
   *
   * 向POS机发送ACK字节表示已成功接收数据
   */
  async #sendAck(): Promise<void> {
    try {
      if (this.#writer) {
        await this.#writer.write(new Uint8Array([ACK]));
        this.#log("发送ACK确认");
      }
    } catch (error) {
      this.#error("发送ACK时出错:", error);
    }
  }

  /**
   * 处理读取器错误
   *
   * 当串口读取发生错误时的处理逻辑
   *
   * @param error 错误对象
   */
  #handleReaderError(error: any): void {
    this.#error("读取器错误:", error);

    // 停止持续读取
    this.#stopContinuousReader();

    // 更新连接状态
    this.#setSerialConnectionStatus({
      connectionStatus: PosConnectionStatus.ERROR,
      healthStatus: PosHealthStatus.FAIL,
    });

    // 清理所有待处理的请求
    this.#clearRequestQueue("读取器错误，连接已断开");

    // 发送错误事件
    this.#emitEvent("error", new Error(`读取器错误: ${error}`));

    // 断开连接
    this.disconnect();
  }

  /**
   * 清理请求队列
   *
   * 清空所有待处理的请求并返回错误结果
   *
   * @param reason 清理原因
   */
  #clearRequestQueue(reason: string): void {
    this.#log(`清理请求队列: ${reason}`);

    for (const requestItem of this.#requestQueue.values()) {
      // 清除超时定时器
      if (requestItem.timeoutTimer) {
        clearTimeout(requestItem.timeoutTimer);
      }
      // 清除轮询定时器
      this.#stopPollStrategy(requestItem);
      // 返回错误结果
      requestItem.resolve({
        success: false,
        status: "ERROR",
        msg: reason,
      });
    }

    // 清空队列
    this.#requestQueue.clear();
    this.#currentRequest = null;
  }

  /**
   * 设置串口连接状态
   *
   * 更新内部状态并通知所有监听器
   *
   * @param state 新的连接状态
   */
  #setSerialConnectionStatus(state: {
    connectionStatus: PosConnectionStatus;
    healthStatus: PosHealthStatus;
  }): void {
    const oldConnectionStatus = this.connectionStatus;
    const oldHealthStatus = this.healthStatus;
    this.connectionStatus = state.connectionStatus;
    this.healthStatus = state.healthStatus;

    // 只有状态发生变化时才通知监听器
    if (
      oldConnectionStatus !== state.connectionStatus ||
      oldHealthStatus !== state.healthStatus
    ) {
      this.#emitEvent("connectionChange", {
        type: "connectionChange",
        connectionStatus: state.connectionStatus,
        healthStatus: state.healthStatus,
        timestamp: Date.now(),
      });
    }
  }

  /**
   * 验证连接状态
   *
   * 检查串口连接是否可用，如果不可用则抛出错误
   *
   * @throws {Error} 当连接不可用时抛出错误
   */
  async #validateConnection(): Promise<void> {
    if (
      this.connectionStatus !== PosConnectionStatus.CONNECTED ||
      !this.#writer
    ) {
      await this.connect();
    }

    if (this.#isAborting) {
      throw new Error("正在执行中止操作");
    }
  }

  /**
   * 设置串口断开监听器
   *
   * 监听串口断开事件并进行相应处理
   */
  #setupDisconnectListener(): void {
    // 使用 Web Serial API 的全局断连事件
    if (!("serial" in navigator) || !(navigator as any).serial) {
      return;
    }

    const handleDisconnect = (event: any) => {
      // 仅在当前存在串口且为当前串口时处理断连事件
      const disconnectedPort = event?.port;
      if (this.#serialPort && disconnectedPort === this.#serialPort) {
        this.#log("检测到串口断开");
        this.#setSerialConnectionStatus({
          connectionStatus: PosConnectionStatus.DISCONNECTED,
          healthStatus: PosHealthStatus.FAIL,
        });
        this.#cleanup();
      }
    };

    navigator.serial.addEventListener("disconnect", handleDisconnect);
    this.#disconnectListenerCleanup = () => {
      navigator.serial.removeEventListener("disconnect", handleDisconnect);
    };
  }

  /**
   * 清理资源
   *
   * 清理所有相关资源和状态
   */
  #cleanup(): void {
    this.#stopHeartbeat();
    this.#stopContinuousReader();
    this.#clearRequestQueue("连接已断开");
  }

  /**
   * 启动心跳检测
   *
   * 定期发送心跳包检测POS机连接状态
   */
  #startHeartbeat(): void {
    if (this.#heartbeatTimer) {
      return;
    }

    this.#log("启动心跳检测");
    this.#heartbeatTimer = window.setInterval(() => {
      this.#performHeartbeat();
    }, this.#config.heartbeatInterval);
  }

  /**
   * 启动 RETRIEVAL 轮询策略
   *
   * 时序：在主请求收到 ACK 后启动；每隔 `intervalMs` 直发一次 RETRIEVAL，最多 `maxAttempts` 次。
   * 忽略条件：
   * - 单次 RETRIEVAL 请求超时（res.status === "TIMEOUT"）
   * - 返回 STATUS = 97 (ECR_BUSY)
   * - 返回 TXN_STATUS = P (PROCESSING)
   * 非忽略：将当前轮询结果同步完成主请求并停止轮询。
   *
   * 说明：轮询总时长上限为 intervalMs * maxAttempts（默认 60s），小于主请求默认超时（120s），
   * 不会干扰主请求的超时策略；若希望更早结束主请求，可在启动轮询时取消主请求的 timeoutTimer（当前实现保持原策略）。
   */
  #startPollStrategy(sourceRequest: RequestItem): void {
    const baseReq = sourceRequest.request as BaseRequest;
    const txnId = baseReq.TXN_ID;
    if (!txnId || !sourceRequest.poll) return;
    if (sourceRequest.poll.active) return;
    sourceRequest.poll.active = true;
    const run = async () => {
      if (!sourceRequest.poll || !sourceRequest.poll.active) return;
      sourceRequest.poll.attempts += 1;
      try {
        const res = await this.#sendRetrievalImmediate(txnId, sourceRequest.id);
        const data = res.data;
        // 是否忽略本次轮询结果
        const ignore =
          res.status === "TIMEOUT" ||
          data?.STATUS === StatusCode.ECR_BUSY ||
          data?.TXN_STATUS === TransactionStatus.PROCESSING;
        if (!ignore) {
          const isSuccess =
            data?.STATUS === StatusCode.APPROVED &&
            data?.TXN_STATUS === TransactionStatus.APPROVED;
          this.#completeRequest(sourceRequest as any, {
            success: isSuccess,
            status: "COMPLETE",
            data,
            msg: `${POS_STATUS_DESC[data?.STATUS || ""] || ""}: ${
              RESP_CODE_DESC[data?.RESP_CODE || ""] || ""
            } ${data?.RESP_CODE || ""}`,
            statusCode: data?.TXN_STATUS,
          });
          this.#stopPollStrategy(sourceRequest);
          return;
        }
      } catch (e) {
        this.#error("轮询策略执行异常:", e);
      }
      if (
        sourceRequest.poll &&
        sourceRequest.poll.active &&
        sourceRequest.poll.attempts < sourceRequest.poll.maxAttempts
      ) {
        sourceRequest.poll.timer = window.setTimeout(
          run,
          sourceRequest.poll.intervalMs
        );
      } else {
        this.#stopPollStrategy(sourceRequest);
      }
    };
    sourceRequest.poll.timer = window.setTimeout(
      run,
      sourceRequest.poll.intervalMs
    );
  }

  /**
   * 停止 RETRIEVAL 轮询策略
   *
   * 清理定时器与激活标记；在主请求完成或达到最大轮询次数时调用。
   */
  #stopPollStrategy(sourceRequest: RequestItem): void {
    this.#log("停止轮询策略", sourceRequest.id);
    if (!sourceRequest.poll) return;
    sourceRequest.poll.active = false;
    if (sourceRequest.poll.timer) {
      clearTimeout(sourceRequest.poll.timer);
      sourceRequest.poll.timer = undefined;
    }
    sourceRequest.poll = undefined;
  }

  /**
   * 轮询策略的 ACK 通知（用于与设备握手节奏对齐）
   *
   * 若轮询尚未激活则启动；已激活则不做额外处理。
   */
  #notifyPollAck(sourceRequest: RequestItem): void {
    if (!sourceRequest.poll) return;
    if (!sourceRequest.poll.active) {
      this.#startPollStrategy(sourceRequest);
    }
  }

  /**
   * 停止心跳检测
   *
   * 清除心跳定时器并重置相关状态
   */
  #stopHeartbeat(): void {
    if (this.#heartbeatTimer) {
      clearInterval(this.#heartbeatTimer);
      this.#heartbeatTimer = null;
      this.#heartbeatPending = false;
      this.#log("停止心跳检测");
    }
  }

  /**
   * 执行心跳检测
   *
   * 发送心跳包并处理响应
   */
  async #performHeartbeat(): Promise<void> {
    // 如果上一次心跳还在进行中或存在在途请求，跳过本次
    if (this.#heartbeatPending || this.#currentRequest) {
      this.#log("上一次心跳检测仍在进行中，跳过本次检测");
      return;
    }

    // 如果连接状态不正常，跳过心跳
    if (this.connectionStatus !== PosConnectionStatus.CONNECTED) {
      return;
    }

    this.#heartbeatPending = true;

    try {
      this.#log("执行心跳检测");
      const result = await this.checkHeartbeat();

      if (result.code === 0) {
        this.#log("心跳检测成功");
        this.#setSerialConnectionStatus({
          connectionStatus: PosConnectionStatus.CONNECTED,
          healthStatus: PosHealthStatus.SUCCESS,
        });
      } else {
        this.#handleHeartbeatFailure("心跳检测失败");
      }
    } catch (error) {
      this.#handleHeartbeatFailure(
        error instanceof Error ? error.message : "心跳检测异常"
      );
    } finally {
      this.#heartbeatPending = false;
    }
  }

  /**
   * 处理心跳检测失败
   *
   * 当心跳检测失败时更新健康状态
   *
   * @param reason 失败原因
   */
  #handleHeartbeatFailure(reason: string): void {
    this.#log("心跳检测失败:", reason);
    this.#setSerialConnectionStatus({
      connectionStatus: this.connectionStatus,
      healthStatus: PosHealthStatus.FAIL,
    });
  }

  /**
   * 通知交易记录监听器
   *
   * 向所有注册的交易记录监听器发送事件通知
   *
   * @param event 交易记录事件
   */
  #notifyTransactionRecordListeners(event: TransactionRecordEvent): void {
    this.#emitEvent("transactionRecord", event);
  }

  /**
   * 生成请求ID
   *
   * 生成唯一的请求标识符
   *
   * @returns 请求ID字符串
   */
  #generateRequestId(eventName: string, txnId?: string): string {
    this.#requestSequence += 1;
    return `${eventName}_${txnId ?? "none"}_${this.#requestSequence}`;
  }

  /**
   * 调试日志输出
   *
   * 当启用调试模式时输出日志信息
   *
   * @param args 日志参数
   */
  #log(...args: any[]): void {
    if (
      this.#config.debug &&
      !args.find(
        (arg) =>
          arg?.toString().includes("ECHO") ||
          arg?.toString().includes("心跳") ||
          arg?.toString().includes("ACK")
      )
    ) {
      const nowDate = dayjs().format("HH:mm:ss.SSS");
      const logStr = `[log: ${nowDate}] ${args.join(" ")}`;
      this.#logQueue.push(logStr);
      console.log(logStr);
    }
  }

  /**
   * 错误日志输出
   *
   * 输出错误信息到控制台
   *
   * @param args 错误参数
   */
  #error(...args: unknown[]): void {
    const nowDate = dayjs().format("HH:mm:ss.SSS");
    const logStr = `[error: ${nowDate}] ${args.join(" ")}`;
    this.#logQueue.push(logStr);
    console.error(logStr);
  }

  /**
   * 日志队列定时上报
   */
  #logQueueUpload(): void {
    if (!this.#logQueue.length) {
      return;
    }
    Http_postLogReport({
      param: this.#logQueue.join("\n"),
    });
    this.#logQueue = [];
  }
}

// 创建默认实例
const n5PosManagerV2 = new N5PosManagerV2({
  debug: true,
});

export default n5PosManagerV2;
